/**
 * TeamCreateModal — create (and edit) a Team.
 *
 * Per TEAMS-SPEC.md:
 *   - Admins see the full department dropdown.
 *   - Department heads are locked to their own department (chip, not select).
 *   - Auto-preview of DEPARTMENT_HEAD from Department.headId.
 *   - If target department has no head → block creation with red warning.
 *   - Edit mode: department is locked for everyone (immutable in v1).
 *
 * Submits via createTeam / updateTeam from `@/api/teams`.
 */
import { useEffect, useMemo, useState } from 'react';
import { X, Loader2, AlertCircle, UserCircle2 } from 'lucide-react';
import { api } from '@/utils/api';
import { createTeam, updateTeam, type Team } from '@/api/teams';
import { useTeamPermissions } from '@/hooks/useTeamPermissions';
import { Modal, useModalClose } from '@/components/ui/Modal';

interface Department {
  id: string;
  name: string;
  code?: string | null;
  headId?: string | null;
  head?: {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  } | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: (team: Team) => void;
  /** Non-null when editing, null when creating. */
  editing?: Team | null;
  /** Optional: if dept-head, pre-set to their own department. */
  lockedDepartmentId?: string;
}

export function TeamCreateModal({
  open,
  onClose,
  onSaved,
  editing,
  lockedDepartmentId,
}: Props) {
  const perms = useTeamPermissions();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [deptId, setDeptId] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEdit = !!editing;

  // Reset when opened
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setDeptId(editing.departmentId);
      setName(editing.name);
      setCode(editing.code ?? '');
      setDescription(editing.description ?? '');
    } else {
      // Default to locked dept (dept-head) or the only dept-head dept,
      // or empty when admin has to pick.
      const defaultDept = lockedDepartmentId
        ?? (perms.myDeptHeadOf.length === 1 && !perms.isAdmin
          ? perms.myDeptHeadOf[0]
          : '');
      setDeptId(defaultDept);
      setName('');
      setCode('');
      setDescription('');
    }
    setError(null);
  }, [open, editing, lockedDepartmentId, perms.isAdmin, perms.myDeptHeadOf]);

  // Load departments (with head info) once modal opens
  useEffect(() => {
    if (!open) return;
    let cancel = false;
    (async () => {
      try {
        const res = await api.get('/departments');
        if (cancel) return;
        const list: Department[] = res.data?.data ?? res.data ?? [];
        setDepartments(list);
      } catch {
        if (!cancel) setDepartments([]);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [open]);

  // Which departments can this user create in?
  const selectableDepartments = useMemo(() => {
    if (perms.isAdmin) return departments;
    return departments.filter((d) => perms.myDeptHeadOf.includes(d.id));
  }, [departments, perms.isAdmin, perms.myDeptHeadOf]);

  const selectedDept = useMemo(
    () => departments.find((d) => d.id === deptId) || null,
    [departments, deptId],
  );

  const headMissing = !!selectedDept && !selectedDept.headId;
  const deptLocked = isEdit || !!lockedDepartmentId
    || (!perms.isAdmin && perms.myDeptHeadOf.length <= 1);

  const canSubmit =
    !!deptId
    && name.trim().length > 0
    && name.trim().length <= 80
    && (isEdit || !headMissing)
    && !saving;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      const trimmedCode = code.trim() ? code.trim().toUpperCase() : undefined;
      let team: Team;
      if (isEdit && editing) {
        team = await updateTeam(editing.id, {
          name: name.trim(),
          code: trimmedCode,
          description: description.trim() || undefined,
        });
      } else {
        team = await createTeam({
          departmentId: deptId,
          name: name.trim(),
          code: trimmedCode,
          description: description.trim() || undefined,
        });
      }
      onSaved(team);
      onClose();
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.error || err?.response?.data?.message;
      if (status === 409) {
        setError(msg || 'A team with this code already exists in this tenant.');
      } else if (status === 400) {
        setError(msg || 'Validation failed. Check the fields below.');
      } else {
        setError(msg || 'Failed to save team. Please retry.');
      }
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const head = selectedDept?.head;
  const headName =
    head && (head.firstName || head.lastName)
      ? `${head.firstName ?? ''} ${head.lastName ?? ''}`.trim()
      : head?.email ?? '';

  return (
    <Modal
      open
      onClose={onClose}
      className="aura-root !max-w-[560px] !rounded-[14px] !border-[var(--line)] !bg-[var(--ivory-1)] !shadow-[var(--sh-3)]"
    >
      <>
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4"
          style={{ borderBottom: '1px solid var(--line)' }}
        >
          <h2
            style={{
              fontFamily: 'var(--ff-serif)',
              fontSize: 20,
              color: 'var(--ink-0)',
              margin: 0,
            }}
          >
            {isEdit ? 'Edit team' : 'New team'}
          </h2>
          <CloseButton className="btn btn-ghost" aria-label="Close">
            <X size={16} />
          </CloseButton>
        </div>

        {/* Body */}
        <div className="px-5 py-5" style={{ display: 'grid', gap: 16 }}>
          {/* Department field */}
          <div>
            <Label>Department</Label>
            {deptLocked ? (
              selectedDept ? (
                <div className="row center gap-2">
                  <span className="chip chip-amber">
                    {selectedDept.name}
                    {selectedDept.code ? ` · ${selectedDept.code}` : ''}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                    {isEdit
                      ? 'Moving a team across departments is not supported.'
                      : 'You can only create teams in your own department.'}
                  </span>
                </div>
              ) : (
                <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                  Loading department…
                </span>
              )
            ) : (
              <select
                value={deptId}
                onChange={(e) => setDeptId(e.target.value)}
                className="w-full"
                style={{
                  background: 'var(--ivory-0)',
                  border: '1px solid var(--line)',
                  borderRadius: 'var(--r-sm)',
                  padding: '8px 10px',
                  fontSize: 13,
                  color: 'var(--ink-0)',
                }}
              >
                <option value="">Select a department…</option>
                {selectableDepartments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                    {d.code ? ` · ${d.code}` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Head preview / warning */}
          {selectedDept && (
            <div
              className="card"
              style={{
                padding: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                background: headMissing ? 'var(--danger-bg)' : 'var(--ivory-2)',
                borderColor: headMissing ? '#EFCFCA' : 'var(--line)',
              }}
            >
              {headMissing ? (
                <>
                  <AlertCircle size={18} color="var(--danger)" />
                  <div style={{ fontSize: 12, color: 'var(--danger)' }}>
                    This department has no Department Head. Set one in{' '}
                    <strong>Departments</strong> before creating a team.
                  </div>
                </>
              ) : (
                <>
                  <UserCircle2 size={22} color="var(--forest-600)" />
                  <div className="grow">
                    <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                      Auto-assigned{' '}
                      <span style={{ color: 'var(--forest-600)' }}>
                        Department Head
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--ink-0)', fontWeight: 500 }}>
                      {headName || 'Department Head'}
                    </div>
                    {head?.email && (
                      <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                        {head.email}
                      </div>
                    )}
                  </div>
                  <span
                    className="chip"
                    style={{ fontSize: 10 }}
                    title="Assigned from Department Head. Change via Departments."
                  >
                    Via Departments
                  </span>
                </>
              )}
            </div>
          )}

          {/* Name */}
          <div>
            <Label required>Team name</Label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              placeholder="e.g. Line A"
              className="w-full"
              style={inputStyle}
            />
            <div style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 4 }}>
              {name.length}/80
            </div>
          </div>

          {/* Code */}
          <div>
            <Label>Code (optional)</Label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onBlur={() => setCode((c) => c.trim().toUpperCase())}
              maxLength={16}
              placeholder="MFG-A"
              className="w-full mono"
              style={{ ...inputStyle, fontFamily: 'var(--ff-mono)' }}
            />
            <div style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 4 }}>
              Unique within your tenant. Auto-uppercases.
            </div>
          </div>

          {/* Description */}
          <div>
            <Label>Description (optional)</Label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full"
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'var(--ff-sans)' }}
            />
          </div>

          {/* Error */}
          {error && (
            <div
              className="row center gap-2"
              style={{
                padding: 10,
                borderRadius: 'var(--r-sm)',
                background: 'var(--danger-bg)',
                color: 'var(--danger)',
                fontSize: 12,
              }}
            >
              <AlertCircle size={14} />
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="row between center px-5 py-4"
          style={{ borderTop: '1px solid var(--line)' }}
        >
          <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
            {isEdit
              ? 'Changes apply immediately.'
              : 'The Department Head will be auto-added on create.'}
          </div>
          <div className="row gap-2">
            <CloseButton className="btn btn-outline">
              Cancel
            </CloseButton>
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={!canSubmit}
              style={{ opacity: canSubmit ? 1 : 0.5 }}
            >
              {saving && <Loader2 size={12} className="animate-spin" />}
              {isEdit ? 'Save changes' : 'Create team'}
            </button>
          </div>
        </div>
      </>
    </Modal>
  );
}

// ── Local atoms ─────────────────────────────────────────────────────────────
function CloseButton({
  className,
  children,
  'aria-label': ariaLabel,
}: {
  className?: string;
  children: React.ReactNode;
  'aria-label'?: string;
}) {
  const { requestClose } = useModalClose();
  return (
    <button type="button" className={className} aria-label={ariaLabel} onClick={requestClose}>
      {children}
    </button>
  );
}

function Label({
  children,
  required,
}: {
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: 'var(--ink-3)',
        marginBottom: 6,
      }}
    >
      {children}
      {required && <span style={{ color: 'var(--danger)' }}> *</span>}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  background: 'var(--ivory-0)',
  border: '1px solid var(--line)',
  borderRadius: 'var(--r-sm)',
  padding: '8px 10px',
  fontSize: 13,
  color: 'var(--ink-0)',
  outline: 'none',
};
